import { action } from '@ember/object';
import { isPresent } from '@ember/utils';
import ensureSafeComponent from '@embroider/util/_app_/helpers/ensure-safe-component.js';
import Component from '@glimmer/component';
import or from 'ember-truth-helpers/helpers/or';
import BvSelectMulti from 'volta/components/bv-select-multi';
import BvSelect from 'volta/components/bv-select';
import orElse from 'volta/helpers/or-else';
import tElse from 'volta/helpers/t-else';

export interface ISelectFilterDefinitionArgs {
  possibleValues: TSelectOption[];
  searchField?: string;
  selectedPath?: string;
  renderInPlace?: boolean;
  horizontalPosition?: 'left' | 'right' | 'auto';
  valueProp?: keyof TSelectOption;
}

export default class FiltersSelectFilterComponent extends Component<
  FilterComponentSignature<ISelectFilterDefinitionArgs>
> {
  <template>
    {{! @glint-nocheck: not typesafe yet }}
    {{#if @multiple}}
      <BvSelectMulti
        @fill={{true}}
        @options={{@componentArgs.possibleValues}}
        @selected={{this.selected}}
        @selectedPath={{this.selectedPath}}
        @searchField={{this.searchField}}
        @searchEnabled={{true}}
        @renderInPlace={{orElse @componentArgs.renderInPlace @renderInPlace}}
        @horizontalPosition={{or @componentArgs.horizontalPosition @horizontalPosition}}
        @matchTriggerWidth={{orElse
          (orElse @componentArgs.matchTriggerWidth @matchTriggerWidth)
          true
        }}
        @allowClear={{@allowClear}}
        @onChange={{this.valuesDidChange}}
        @optionKey={{orElse @componentArgs.optionKey 'key'}}
        @dataName={{@name}}
        as |option|
      >
        {{#if (has-block)}}
          {{yield option}}
        {{else if @componentArgs.itemComponent}}
          {{component (ensureSafeComponent @componentArgs.itemComponent this) option=option}}
        {{else if option.label}}
          {{tElse option.label}}
        {{else if option.value}}
          {{tElse option.value}}
        {{else if option.key}}
          {{option.key}}
        {{else}}
          {{option}}
        {{/if}}
      </BvSelectMulti>
    {{else}}
      <BvSelect
        @fill={{true}}
        @options={{@componentArgs.possibleValues}}
        @selected={{this.selected}}
        @searchField={{this.searchField}}
        @searchEnabled={{true}}
        @renderInPlace={{orElse @componentArgs.renderInPlace @renderInPlace}}
        @horizontalPosition={{or @componentArgs.horizontalPosition @horizontalPosition}}
        @matchTriggerWidth={{orElse
          (orElse @componentArgs.matchTriggerWidth @matchTriggerWidth)
          true
        }}
        @allowClear={{@allowClear}}
        @onChange={{this.singleValueDidChange}}
        @optionKey={{orElse @componentArgs.optionKey 'key'}}
        @dataName={{@name}}
        as |option|
      >
        {{#if (has-block)}}
          {{yield option}}
        {{else if @componentArgs.itemComponent}}
          {{component (ensureSafeComponent @componentArgs.itemComponent this) option=option}}
        {{else if option.label}}
          {{tElse option.label}}
        {{else if option.value}}
          {{tElse option.value}}
        {{else if option.key}}
          {{option.key}}
        {{else}}
          {{option}}
        {{/if}}
      </BvSelect>
    {{/if}}
  </template>
  get searchField() {
    return this.args.componentArgs.searchField ?? 'key';
  }

  get selectedPath() {
    return this.args.componentArgs.selectedPath;
  }

  get selected() {
    const { componentArgs, values = [], multiple } = this.args;
    const valuesStr = new Set(values.map(String));

    if (multiple) {
      return componentArgs?.possibleValues?.filter((o) => {
        const v = this.getValue(o);
        return v && valuesStr.has(v);
      });
    } else {
      return values.length > 0
        ? componentArgs?.possibleValues?.find((o) => this.getValue(o) === values[0]?.toString())
        : undefined;
    }
  }

  getValue(o: TSelectOption) {
    const v = o[this.args.componentArgs.valueProp ?? 'key'];
    return isPresent(v) ? `${v}` : undefined;
  }

  getValues(options: (TSelectOption | undefined)[]) {
    return options.reduce((acc: string[], o) => {
      const v = o && this.getValue(o);
      if (isPresent(v)) {
        acc.push(v as string);
      }
      return acc;
    }, []);
  }

  @action
  valuesDidChange(options: TSelectOption[]) {
    this.args.onChange?.(this.getValues(options));
  }

  @action
  singleValueDidChange(option?: TSelectOption) {
    this.args.onChange?.(this.getValues([option]));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Filters::SelectFilter': typeof FiltersSelectFilterComponent;
    'filters/select-filter': typeof FiltersSelectFilterComponent;
  }
}
